import { useEffect, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { toast } from 'react-toastify'
import AdminRichTextEditor from '../../components/admin/AdminRichTextEditor'
import {
  AdminApi,
  AdminQuestionGroup,
  AdminQuestionListItem,
  AdminQuestionTopic,
} from '../../services/adminApi'

const TYPE_LABEL: Record<string, string> = {
  SINGLE_CHOICE: '1 đáp án',
  CHOICE: '1 đáp án',
  MULTIPLE_CHOICE: 'Nhiều đáp án',
  ESSAY: 'Tự luận',
}

const stripHtml = (html: string) =>
  String(html || '')
    .replace(/<[^>]*>/g, ' ')
    .replace(/&nbsp;/gi, ' ')
    .replace(/\s+/g, ' ')
    .trim()

const fieldClass =
  'w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 placeholder:text-slate-400 focus:border-emerald-700 focus:outline-none focus:ring-1 focus:ring-emerald-700'
const labelClass = 'mb-1 block text-xs font-semibold text-slate-700'
const panelClass = 'rounded-xl border border-slate-200 bg-white p-4 shadow-sm'
const btnPrimary =
  'rounded-lg bg-emerald-800 px-4 py-2 text-sm font-semibold text-white hover:bg-emerald-900 disabled:opacity-50'
const btnSecondary =
  'rounded-lg border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-800 hover:bg-slate-50 disabled:opacity-50'

type TabKey = 'questions' | 'case-study'

const AdminQuestionBankPage = () => {
  const [searchParams, setSearchParams] = useSearchParams()
  const tab: TabKey = searchParams.get('tab') === 'case-study' ? 'case-study' : 'questions'

  const setTab = (next: TabKey) => {
    const sp = new URLSearchParams(searchParams)
    if (next === 'case-study') sp.set('tab', 'case-study')
    else sp.delete('tab')
    if (next !== 'case-study') sp.delete('groupId')
    setSearchParams(sp, { replace: true })
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Ngân hàng câu hỏi</h1>
          <p className="mt-1 text-sm text-slate-600">
            Câu hỏi lẻ hoặc nhóm (case study HTML) — trắc nghiệm / tự luận.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          {tab === 'case-study' ? (
            <button
              type="button"
              onClick={() => {
                const sp = new URLSearchParams(searchParams)
                sp.set('tab', 'case-study')
                sp.set('create', '1')
                setSearchParams(sp)
              }}
              className={btnPrimary}
            >
              Tạo case study
            </button>
          ) : (
            <Link to="/admin/question-bank/new" className={btnPrimary}>
              Tạo câu hỏi
            </Link>
          )}
        </div>
      </div>

      <div className="flex gap-1 border-b border-slate-200">
        <button
          type="button"
          onClick={() => setTab('questions')}
          className={`-mb-px border-b-2 px-4 py-2.5 text-sm font-semibold transition-colors ${
            tab === 'questions'
              ? 'border-emerald-800 text-emerald-900'
              : 'border-transparent text-slate-500 hover:text-slate-800'
          }`}
        >
          Câu hỏi
        </button>
        <button
          type="button"
          onClick={() => setTab('case-study')}
          className={`-mb-px border-b-2 px-4 py-2.5 text-sm font-semibold transition-colors ${
            tab === 'case-study'
              ? 'border-emerald-800 text-emerald-900'
              : 'border-transparent text-slate-500 hover:text-slate-800'
          }`}
        >
          Case study
        </button>
      </div>

      {tab === 'questions' ? <QuestionsTab /> : <CaseStudyTab />}
    </div>
  )
}

/* ─── Tab: danh sách câu hỏi ─────────────────────────────────────────── */

const QuestionsTab = () => {
  const [loading, setLoading] = useState(false)
  const [items, setItems] = useState<AdminQuestionListItem[]>([])
  const [topics, setTopics] = useState<AdminQuestionTopic[]>([])
  const [groups, setGroups] = useState<AdminQuestionGroup[]>([])
  const [page, setPage] = useState(1)
  const [totalPages, setTotalPages] = useState(0)
  const [total, setTotal] = useState(0)
  const [q, setQ] = useState('')
  const [topicId, setTopicId] = useState<number | undefined>()
  const [groupId, setGroupId] = useState<number | undefined>()
  const [questionType, setQuestionType] = useState('')
  const [level, setLevel] = useState('')
  const [isActive, setIsActive] = useState<number>(1)
  const [newTopicName, setNewTopicName] = useState('')
  const [creatingTopic, setCreatingTopic] = useState(false)

  const loadMeta = async () => {
    try {
      const [tRes, gRes] = await Promise.all([
        AdminApi.listQuestionTopics(),
        AdminApi.listQuestionGroups({ limit: 100, isActive: 1 }),
      ])
      setTopics(tRes.data || [])
      setGroups(gRes.data?.items || [])
    } catch {
      toast.error('Không tải được chủ đề / nhóm')
    }
  }

  const loadData = async (overrides?: {
    page?: number
    q?: string
    topicId?: number | undefined
    groupId?: number | undefined
    questionType?: string
    level?: string
    isActive?: number
  }) => {
    const nextPage = overrides?.page ?? page
    const nextQ = overrides && 'q' in overrides ? overrides.q! : q
    const nextTopic = overrides && 'topicId' in overrides ? overrides.topicId : topicId
    const nextGroup = overrides && 'groupId' in overrides ? overrides.groupId : groupId
    const nextType =
      overrides && 'questionType' in overrides ? overrides.questionType! : questionType
    const nextLevel = overrides && 'level' in overrides ? overrides.level! : level
    const nextActive = overrides && 'isActive' in overrides ? overrides.isActive! : isActive

    setLoading(true)
    try {
      const res = await AdminApi.listQuestions({
        q: nextQ?.trim() || undefined,
        topicId: nextTopic,
        groupId: nextGroup,
        questionType: nextType || undefined,
        level: nextLevel || undefined,
        isActive: nextActive,
        page: nextPage,
        limit: 20,
      })
      setItems(res.data.items)
      setTotalPages(res.data.pagination.totalPages)
      setTotal(res.data.pagination.total)
    } catch {
      toast.error('Không tải được ngân hàng câu hỏi')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    void loadMeta()
  }, [])

  useEffect(() => {
    void loadData({ page })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page])

  const applyFilters = () => {
    if (page === 1) void loadData({ page: 1 })
    else setPage(1)
  }

  const resetFilters = () => {
    setQ('')
    setTopicId(undefined)
    setGroupId(undefined)
    setQuestionType('')
    setLevel('')
    setIsActive(1)
    if (page !== 1) setPage(1)
    void loadData({
      page: 1,
      q: '',
      topicId: undefined,
      groupId: undefined,
      questionType: '',
      level: '',
      isActive: 1,
    })
  }

  const createTopic = async () => {
    const name = newTopicName.trim()
    if (!name) {
      toast.error('Nhập tên chủ đề')
      return
    }
    setCreatingTopic(true)
    try {
      const res = await AdminApi.createQuestionTopic({ name })
      if (res.success) {
        toast.success('Đã tạo chủ đề')
        setNewTopicName('')
        await loadMeta()
      } else {
        toast.error(res.message || 'Tạo chủ đề thất bại')
      }
    } catch {
      toast.error('Tạo chủ đề thất bại')
    } finally {
      setCreatingTopic(false)
    }
  }

  const toggleActive = async (item: AdminQuestionListItem) => {
    const next = item.isActive ? 0 : 1
    try {
      const res = await AdminApi.setQuestionActive(item.id, next)
      if (res.success) {
        toast.success(res.message || 'Đã cập nhật')
        void loadData()
      } else {
        toast.error(res.message || 'Cập nhật thất bại')
      }
    } catch {
      toast.error('Cập nhật thất bại')
    }
  }

  return (
    <div className="space-y-4">
      <div className={`flex flex-wrap items-end gap-3 ${panelClass}`}>
        <div className="min-w-[180px] flex-1">
          <label className={labelClass}>Tìm nội dung</label>
          <input
            type="text"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && applyFilters()}
            placeholder="Từ khóa trong câu hỏi…"
            className={fieldClass}
          />
        </div>
        <div className="w-full sm:w-40">
          <label className={labelClass}>Loại</label>
          <select
            value={questionType}
            onChange={(e) => setQuestionType(e.target.value)}
            className={fieldClass}
          >
            <option value="">Tất cả</option>
            <option value="SINGLE_CHOICE">1 đáp án</option>
            <option value="MULTIPLE_CHOICE">Nhiều đáp án</option>
            <option value="ESSAY">Tự luận</option>
          </select>
        </div>
        <div className="w-full sm:w-36">
          <label className={labelClass}>Độ khó</label>
          <select value={level} onChange={(e) => setLevel(e.target.value)} className={fieldClass}>
            <option value="">Tất cả</option>
            <option value="EASY">EASY</option>
            <option value="MEDIUM">MEDIUM</option>
            <option value="HARD">HARD</option>
          </select>
        </div>
        <div className="w-full sm:w-48">
          <label className={labelClass}>Chủ đề</label>
          <select
            value={topicId ?? ''}
            onChange={(e) => setTopicId(e.target.value ? Number(e.target.value) : undefined)}
            className={fieldClass}
          >
            <option value="">Tất cả</option>
            {topics.map((t) => (
              <option key={t.id} value={t.id}>
                {t.name}
              </option>
            ))}
          </select>
        </div>
        <div className="w-full sm:w-48">
          <label className={labelClass}>Case study</label>
          <select
            value={groupId ?? ''}
            onChange={(e) => setGroupId(e.target.value ? Number(e.target.value) : undefined)}
            className={fieldClass}
          >
            <option value="">Tất cả</option>
            {groups.map((g) => (
              <option key={g.id} value={g.id}>
                #{g.id} — {g.preview || 'Case study'}
              </option>
            ))}
          </select>
        </div>
        <div className="w-full sm:w-36">
          <label className={labelClass}>Trạng thái</label>
          <select
            value={isActive}
            onChange={(e) => setIsActive(Number(e.target.value))}
            className={fieldClass}
          >
            <option value={1}>Active</option>
            <option value={0}>Tắt</option>
          </select>
        </div>
        <div className="flex gap-2">
          <button type="button" onClick={applyFilters} className={btnPrimary}>
            Lọc
          </button>
          <button type="button" onClick={resetFilters} className={btnSecondary}>
            Reset
          </button>
        </div>
      </div>

      <div className="flex flex-wrap items-end gap-2 rounded-xl border border-dashed border-slate-300 bg-slate-50 p-3">
        <div className="min-w-[200px] flex-1">
          <label className={labelClass}>Thêm chủ đề nhanh</label>
          <input
            type="text"
            value={newTopicName}
            onChange={(e) => setNewTopicName(e.target.value)}
            placeholder="Tên chủ đề"
            className={fieldClass}
          />
        </div>
        <button
          type="button"
          disabled={creatingTopic}
          onClick={() => void createTopic()}
          className={btnSecondary}
        >
          {creatingTopic ? 'Đang tạo…' : 'Thêm chủ đề'}
        </button>
        <p className="w-full text-xs text-slate-500">{total} câu hỏi</p>
      </div>

      <div className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
        <div className="overflow-x-auto">
          <table className="min-w-full text-left text-sm">
            <thead className="border-b border-slate-200 bg-slate-50 text-xs uppercase tracking-wide text-slate-600">
              <tr>
                <th className="px-4 py-3 font-semibold">ID</th>
                <th className="px-4 py-3 font-semibold">Nội dung</th>
                <th className="px-4 py-3 font-semibold">Loại</th>
                <th className="px-4 py-3 font-semibold">Độ khó</th>
                <th className="px-4 py-3 font-semibold">Case study</th>
                <th className="px-4 py-3 font-semibold">Đáp án</th>
                <th className="px-4 py-3 font-semibold">TT</th>
                <th className="px-4 py-3 font-semibold" />
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={8} className="px-4 py-8 text-center text-slate-500">
                    Đang tải…
                  </td>
                </tr>
              ) : items.length === 0 ? (
                <tr>
                  <td colSpan={8} className="px-4 py-8 text-center text-slate-500">
                    Không có câu hỏi
                  </td>
                </tr>
              ) : (
                items.map((item) => (
                  <tr key={item.id} className="border-b border-slate-100 hover:bg-slate-50/80">
                    <td className="px-4 py-3 font-mono text-xs text-slate-700">{item.id}</td>
                    <td className="max-w-md px-4 py-3">
                      <p className="line-clamp-2 text-slate-900">{item.preview || '—'}</p>
                      {item.topicName && (
                        <p className="mt-0.5 text-xs text-slate-500">{item.topicName}</p>
                      )}
                    </td>
                    <td className="whitespace-nowrap px-4 py-3 text-slate-700">
                      {TYPE_LABEL[String(item.questionType).toUpperCase()] || item.questionType}
                    </td>
                    <td className="px-4 py-3 text-slate-700">{item.level}</td>
                    <td className="px-4 py-3">
                      {item.groupId ? (
                        <Link
                          to={`/admin/question-bank?tab=case-study&groupId=${item.groupId}`}
                          className="font-medium text-emerald-800 hover:underline"
                        >
                          #{item.groupId}
                        </Link>
                      ) : (
                        <span className="text-slate-400">—</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-slate-700">{item.answersCount ?? 0}</td>
                    <td className="px-4 py-3">
                      <button
                        type="button"
                        onClick={() => void toggleActive(item)}
                        className={`rounded-md px-2 py-0.5 text-xs font-semibold ${
                          item.isActive
                            ? 'bg-emerald-100 text-emerald-900'
                            : 'bg-slate-200 text-slate-600'
                        }`}
                      >
                        {item.isActive ? 'Active' : 'Off'}
                      </button>
                    </td>
                    <td className="whitespace-nowrap px-4 py-3 text-right">
                      <Link
                        to={`/admin/question-bank/${item.id}/edit`}
                        className="text-sm font-semibold text-emerald-800 hover:underline"
                      >
                        Sửa
                      </Link>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        {totalPages > 1 && (
          <div className="flex items-center justify-between border-t border-slate-200 px-4 py-3 text-sm">
            <button
              type="button"
              disabled={page <= 1}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              className={`${btnSecondary} disabled:opacity-40`}
            >
              Trước
            </button>
            <span className="text-slate-600">
              Trang {page}/{totalPages}
            </span>
            <button
              type="button"
              disabled={page >= totalPages}
              onClick={() => setPage((p) => p + 1)}
              className={`${btnSecondary} disabled:opacity-40`}
            >
              Sau
            </button>
          </div>
        )}
      </div>
    </div>
  )
}

/* ─── Tab: Case study ────────────────────────────────────────────────── */

const CaseStudyTab = () => {
  const [searchParams, setSearchParams] = useSearchParams()
  const selectedId = searchParams.get('groupId')
    ? Number(searchParams.get('groupId'))
    : null
  const wantCreate = searchParams.get('create') === '1'

  const [loading, setLoading] = useState(false)
  const [items, setItems] = useState<AdminQuestionGroup[]>([])
  const [q, setQ] = useState('')
  const [page, setPage] = useState(1)
  const [totalPages, setTotalPages] = useState(0)

  const [detail, setDetail] = useState<AdminQuestionGroup | null>(null)
  const [detailLoading, setDetailLoading] = useState(false)
  const [editing, setEditing] = useState(false)
  const [nameHtml, setNameHtml] = useState('')
  const [saving, setSaving] = useState(false)

  const [showPicker, setShowPicker] = useState(false)
  const [pickerQ, setPickerQ] = useState('')
  const [pickerItems, setPickerItems] = useState<AdminQuestionListItem[]>([])
  const [pickerLoading, setPickerLoading] = useState(false)

  const selectGroup = (id: number | null, create = false) => {
    const sp = new URLSearchParams(searchParams)
    sp.set('tab', 'case-study')
    if (id) sp.set('groupId', String(id))
    else sp.delete('groupId')
    if (create) sp.set('create', '1')
    else sp.delete('create')
    setSearchParams(sp, { replace: true })
  }

  const loadList = async (overrides?: { page?: number; q?: string }) => {
    const nextPage = overrides?.page ?? page
    const nextQ = overrides && 'q' in overrides ? overrides.q! : q
    setLoading(true)
    try {
      const res = await AdminApi.listQuestionGroups({
        q: nextQ?.trim() || undefined,
        page: nextPage,
        limit: 20,
      })
      setItems(res.data.items)
      setTotalPages(res.data.pagination.totalPages)
    } catch {
      toast.error('Không tải được danh sách case study')
    } finally {
      setLoading(false)
    }
  }

  const loadDetail = async (id: number) => {
    setDetailLoading(true)
    try {
      const res = await AdminApi.getQuestionGroup(id)
      setDetail(res.data)
      setNameHtml(res.data.name || '')
      setEditing(false)
      setShowPicker(false)
    } catch {
      toast.error('Không tải được case study')
      setDetail(null)
    } finally {
      setDetailLoading(false)
    }
  }

  useEffect(() => {
    void loadList({ page })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page])

  useEffect(() => {
    if (wantCreate) {
      setDetail(null)
      setNameHtml('')
      setEditing(true)
      setShowPicker(false)
      return
    }
    if (selectedId && selectedId > 0) {
      void loadDetail(selectedId)
    } else {
      setDetail(null)
      setEditing(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedId, wantCreate])

  const saveCaseStudy = async () => {
    if (!stripHtml(nameHtml)) {
      toast.error('Nội dung case study không được trống')
      return
    }
    setSaving(true)
    try {
      const isNew = wantCreate || !selectedId
      const res = isNew
        ? await AdminApi.createQuestionGroup({ name: nameHtml })
        : await AdminApi.updateQuestionGroup(selectedId!, { name: nameHtml })
      if (res.success && res.data?.id) {
        toast.success(res.message || 'Đã lưu')
        selectGroup(res.data.id)
        void loadList()
        void loadDetail(res.data.id)
      } else {
        toast.error(res.message || 'Lưu thất bại')
      }
    } catch (err: unknown) {
      const msg =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ||
        'Lưu thất bại'
      toast.error(msg)
    } finally {
      setSaving(false)
    }
  }

  const removeFromGroup = async (questionId: number) => {
    if (!window.confirm('Gỡ câu hỏi này khỏi case study? (Câu hỏi vẫn còn trong ngân hàng)')) return
    try {
      const res = await AdminApi.updateQuestion(questionId, { groupId: null })
      if (res.success) {
        toast.success('Đã gỡ khỏi case study')
        if (selectedId) void loadDetail(selectedId)
        void loadList()
      } else {
        toast.error(res.message || 'Gỡ thất bại')
      }
    } catch {
      toast.error('Gỡ thất bại')
    }
  }

  const loadPicker = async (search?: string) => {
    setPickerLoading(true)
    try {
      const res = await AdminApi.listQuestions({
        q: search?.trim() || undefined,
        ungrouped: true,
        isActive: 1,
        limit: 30,
      })
      setPickerItems(res.data.items)
    } catch {
      toast.error('Không tải được câu hỏi lẻ')
    } finally {
      setPickerLoading(false)
    }
  }

  const addExistingToGroup = async (questionId: number) => {
    if (!selectedId) return
    try {
      const res = await AdminApi.updateQuestion(questionId, { groupId: selectedId })
      if (res.success) {
        toast.success('Đã thêm vào case study')
        void loadDetail(selectedId)
        void loadList()
        void loadPicker(pickerQ)
      } else {
        toast.error(res.message || 'Thêm thất bại')
      }
    } catch {
      toast.error('Thêm thất bại')
    }
  }

  const showEditor = wantCreate || editing

  return (
    <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.2fr)]">
      {/* Left: list */}
      <div className="space-y-3">
        <div className={`flex flex-wrap items-end gap-3 ${panelClass}`}>
          <div className="min-w-[160px] flex-1">
            <label className={labelClass}>Tìm case study</label>
            <input
              type="text"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  setPage(1)
                  void loadList({ page: 1, q })
                }
              }}
              placeholder="Từ khóa trong tình huống…"
              className={fieldClass}
            />
          </div>
          <button
            type="button"
            onClick={() => {
              setPage(1)
              void loadList({ page: 1 })
            }}
            className={btnPrimary}
          >
            Lọc
          </button>
        </div>

        <div className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
          <table className="min-w-full text-left text-sm">
            <thead className="border-b border-slate-200 bg-slate-50 text-xs uppercase text-slate-600">
              <tr>
                <th className="px-3 py-2.5 font-semibold">ID</th>
                <th className="px-3 py-2.5 font-semibold">Nội dung</th>
                <th className="px-3 py-2.5 font-semibold">Câu</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={3} className="px-3 py-6 text-center text-slate-500">
                    Đang tải…
                  </td>
                </tr>
              ) : items.length === 0 ? (
                <tr>
                  <td colSpan={3} className="px-3 py-6 text-center text-slate-500">
                    Chưa có case study
                  </td>
                </tr>
              ) : (
                items.map((g) => {
                  const active = selectedId === g.id && !wantCreate
                  return (
                    <tr
                      key={g.id}
                      onClick={() => selectGroup(g.id)}
                      className={`cursor-pointer border-b border-slate-100 ${
                        active ? 'bg-emerald-50' : 'hover:bg-slate-50'
                      }`}
                    >
                      <td className="px-3 py-2.5 font-mono text-xs text-slate-700">{g.id}</td>
                      <td className="max-w-xs px-3 py-2.5">
                        <p className="line-clamp-2 text-slate-900">{g.preview || '—'}</p>
                      </td>
                      <td className="px-3 py-2.5 text-slate-700">{g.questionCount ?? 0}</td>
                    </tr>
                  )
                })
              )}
            </tbody>
          </table>
          {totalPages > 1 && (
            <div className="flex items-center justify-between border-t border-slate-200 px-3 py-2 text-sm">
              <button
                type="button"
                disabled={page <= 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                className={`${btnSecondary} px-3 py-1 disabled:opacity-40`}
              >
                Trước
              </button>
              <span className="text-slate-600">
                {page}/{totalPages}
              </span>
              <button
                type="button"
                disabled={page >= totalPages}
                onClick={() => setPage((p) => p + 1)}
                className={`${btnSecondary} px-3 py-1 disabled:opacity-40`}
              >
                Sau
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Right: detail / editor */}
      <div className="space-y-3">
        {!selectedId && !wantCreate && (
          <div className={`${panelClass} text-sm text-slate-600`}>
            Chọn một case study bên trái, hoặc bấm <strong>Tạo case study</strong> để nhập tình huống
            HTML.
          </div>
        )}

        {(showEditor || detail || wantCreate) && (
          <div className={`${panelClass} space-y-3`}>
            <div className="flex flex-wrap items-center justify-between gap-2">
              <h2 className="text-sm font-bold text-slate-900">
                {wantCreate
                  ? 'Tạo case study mới'
                  : editing
                    ? `Sửa case study #${selectedId}`
                    : `Case study #${selectedId}`}
              </h2>
              <div className="flex flex-wrap gap-2">
                {!wantCreate && detail && !editing && (
                  <button type="button" onClick={() => setEditing(true)} className={btnSecondary}>
                    Sửa nội dung
                  </button>
                )}
                {showEditor && (
                  <>
                    <button
                      type="button"
                      disabled={saving}
                      onClick={() => void saveCaseStudy()}
                      className={btnPrimary}
                    >
                      {saving ? 'Đang lưu…' : 'Lưu'}
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        if (wantCreate) selectGroup(null)
                        else if (selectedId) void loadDetail(selectedId)
                        else setEditing(false)
                      }}
                      className={btnSecondary}
                    >
                      Hủy
                    </button>
                  </>
                )}
              </div>
            </div>

            {detailLoading && !wantCreate ? (
              <p className="text-sm text-slate-500">Đang tải…</p>
            ) : showEditor ? (
              <div>
                <label className={labelClass}>Nội dung tình huống (HTML)</label>
                <AdminRichTextEditor
                  value={nameHtml}
                  onChange={setNameHtml}
                  placeholder="Nhập đoạn văn / tình huống case study…"
                />
              </div>
            ) : detail ? (
              <div
                className="prose prose-sm max-w-none rounded-lg border border-slate-200 bg-slate-50 p-3 text-slate-800"
                dangerouslySetInnerHTML={{ __html: detail.name || '' }}
              />
            ) : null}
          </div>
        )}

        {detail && !wantCreate && (
          <div className={`${panelClass} space-y-3`}>
            <div className="flex flex-wrap items-center justify-between gap-2">
              <h3 className="text-sm font-bold text-slate-900">
                Câu hỏi trong case study ({detail.questions?.length ?? 0})
              </h3>
              <div className="flex flex-wrap gap-2">
                <Link
                  to={`/admin/question-bank/new?groupId=${detail.id}&kind=group`}
                  className={btnPrimary}
                >
                  + Tạo câu mới
                </Link>
                <button
                  type="button"
                  onClick={() => {
                    setShowPicker((v) => !v)
                    if (!showPicker) void loadPicker()
                  }}
                  className={btnSecondary}
                >
                  + Thêm câu có sẵn
                </button>
              </div>
            </div>

            {showPicker && (
              <div className="space-y-2 rounded-lg border border-slate-200 bg-slate-50 p-3">
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={pickerQ}
                    onChange={(e) => setPickerQ(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && void loadPicker(pickerQ)}
                    placeholder="Tìm câu hỏi lẻ…"
                    className={fieldClass}
                  />
                  <button
                    type="button"
                    onClick={() => void loadPicker(pickerQ)}
                    className={btnSecondary}
                  >
                    Tìm
                  </button>
                </div>
                {pickerLoading ? (
                  <p className="text-xs text-slate-500">Đang tải…</p>
                ) : pickerItems.length === 0 ? (
                  <p className="text-xs text-slate-500">Không có câu hỏi lẻ phù hợp</p>
                ) : (
                  <ul className="max-h-48 space-y-1 overflow-y-auto text-sm">
                    {pickerItems.map((pq) => (
                      <li
                        key={pq.id}
                        className="flex items-center justify-between gap-2 rounded-md bg-white px-2 py-1.5 border border-slate-100"
                      >
                        <span className="line-clamp-1 text-slate-800">
                          #{pq.id} — {pq.preview || '…'}
                        </span>
                        <button
                          type="button"
                          onClick={() => void addExistingToGroup(pq.id)}
                          className="shrink-0 text-xs font-semibold text-emerald-800 hover:underline"
                        >
                          Thêm
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )}

            {!detail.questions?.length ? (
              <p className="text-sm text-slate-500">Chưa có câu hỏi trong case study này.</p>
            ) : (
              <ul className="divide-y divide-slate-100 rounded-lg border border-slate-200">
                {detail.questions.map((qq) => (
                  <li
                    key={qq.id}
                    className="flex flex-wrap items-center justify-between gap-2 px-3 py-2.5 text-sm"
                  >
                    <div className="min-w-0 flex-1">
                      <p className="line-clamp-2 text-slate-900">
                        <span className="font-mono text-xs text-slate-500">#{qq.id}</span>{' '}
                        {qq.preview || '…'}
                      </p>
                      <p className="mt-0.5 text-xs text-slate-500">
                        {TYPE_LABEL[String(qq.questionType).toUpperCase()] || qq.questionType} ·{' '}
                        {qq.level}
                      </p>
                    </div>
                    <div className="flex shrink-0 gap-3">
                      <Link
                        to={`/admin/question-bank/${qq.id}/edit`}
                        className="text-sm font-semibold text-emerald-800 hover:underline"
                      >
                        Sửa
                      </Link>
                      <button
                        type="button"
                        onClick={() => void removeFromGroup(qq.id)}
                        className="text-sm font-semibold text-red-600 hover:underline"
                      >
                        Gỡ
                      </button>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
      </div>
    </div>
  )
}

export default AdminQuestionBankPage
